import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { StatusBadge, Overline } from "@/components/ui-bits";
import { GitFork, Hammer, HandCoins, Headset, Server } from "lucide-react";

const RICON = { builder: Hammer, seller: HandCoins, support: Headset, api_operator: Server };

export default function SpawnTree() {
  const [agents, setAgents] = useState([]);
  const load = () => api.get("/spawn-tree").then((r) => setAgents(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, []);
  useRealtime(() => load());

  const roots = agents.filter((a) => !a.parent_id);
  const childrenOf = (id) => agents.filter((a) => a.parent_id === id);

  return (
    <div className="space-y-6" data-testid="spawn-tree-page">
      <div>
        <Overline>Agent Hierarchy Graph</Overline>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Spawn Tree</h1>
      </div>

      {roots.length === 0 && (
        <div className="hud-card rounded-lg p-12 text-center">
          <GitFork className="w-10 h-10 text-slate-600 mx-auto" />
          <p className="mt-3 text-slate-400">No agents to graph. Create a parent agent and spawn children to see the hierarchy.</p>
        </div>
      )}

      <div className="space-y-8">
        {roots.map((root) => (
          <div key={root.id} className="hud-card rounded-lg p-6">
            <Node agent={root} />
            <div className="mt-4 ml-6 pl-6 border-l border-dashed border-slate-700 space-y-4">
              {childrenOf(root.id).map((child) => (
                <div key={child.id}>
                  <Node agent={child} child />
                  <div className="mt-3 ml-6 pl-6 border-l border-dashed border-slate-800 space-y-3">
                    {childrenOf(child.id).map((gc) => <Node key={gc.id} agent={gc} child />)}
                  </div>
                </div>
              ))}
              {childrenOf(root.id).length === 0 && <div className="text-xs text-slate-600 font-mono py-2">No children spawned.</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Node({ agent, child }) {
  const Icon = RICON[agent.role] || Hammer;
  const accent = agent.status === "active" ? "#10B981" : agent.status === "frozen" ? "#EF4444" : agent.status === "paused" ? "#F59E0B" : "#334155";
  return (
    <div className={`inline-flex items-center gap-3 rounded-lg border bg-[#080B11] px-4 py-3 ${child ? "border-slate-800" : "border-slate-700"}`} style={{ boxShadow: `inset 3px 0 0 ${accent}` }} data-testid={`tree-node-${agent.id}`}>
      <div className="w-8 h-8 rounded-md bg-slate-800/60 border border-slate-700 flex items-center justify-center">
        <Icon className="w-4 h-4 text-cyan-400" />
      </div>
      <div>
        <div className="font-display font-bold text-sm flex items-center gap-2">
          {agent.name}
          {child && <span className="font-mono text-[9px] text-cyan-400">D{agent.depth}</span>}
        </div>
        <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{agent.role.replace("_", " ")} · wallet {money(agent.wallet_balance)}</div>
      </div>
      <div className="ml-2"><StatusBadge status={agent.status} /></div>
      <div className="ml-2 text-[10px] font-mono text-slate-600">rev {money(agent.revenue_generated)}</div>
    </div>
  );
}
